/**
 * User Management — list view.
 *
 * Lists internal + operational users (everyone except customers) so an
 * admin can pick one to edit. Row-click drills into `UserManagementPage`
 * (`/users/:id`) where profile, BI access and CRM links are all surfaced.
 *
 * Backed by `GET /api/admin/internal-users/` (see `internalUsers.ts`).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ToggleChip } from '@/components/ui/Chip';
import { DataView } from '@/components/data/DataView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useInternalUsers, type InternalUserRow } from '@/api/bi/internalUsers';
import { CreateUserDialog } from './CreateUserDialog';

/**
 * Quick role chips. Mirrors the backend `INTERNAL_ROLES` list (everything
 * except `customer`). Kept local on purpose so the chip ordering reflects
 * how the UI wants to group these — admin-first, then ops.
 */
const ROLE_CHIPS: Array<{ key: string; label: string; role: string }> = [
  { key: 'all', label: 'All', role: '' },
  { key: 'admin', label: 'Admin', role: 'admin' },
  { key: 'manager', label: 'Manager', role: 'manager' },
  { key: 'operation', label: 'Operation', role: 'operation' },
  { key: 'support', label: 'Support', role: 'support' },
  { key: 'account', label: 'Account', role: 'account' },
  { key: 'staff', label: 'Staff', role: 'staff' },
  { key: 'packer', label: 'Packer', role: 'packer' },
  { key: 'rider', label: 'Rider', role: 'rider' },
  { key: 'driver', label: 'Driver', role: 'driver' },
];

/** All choices for the dropdown (overflow beyond the chips). */
const ROLE_DROPDOWN: Array<{ value: string; label: string }> = [
  { value: '__any', label: 'Any role' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'operation', label: 'Operation' },
  { value: 'support', label: 'Support' },
  { value: 'account', label: 'Account' },
  { value: 'staff', label: 'Staff' },
  { value: 'packer', label: 'Packer' },
  { value: 'rider', label: 'Rider' },
  { value: 'driver', label: 'Driver' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'agency', label: 'Agency' },
  { value: 'tagger', label: 'Tagger' },
  { value: 'audit', label: 'Audit' },
  { value: 'guard', label: 'Guard' },
];

export function UsersListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [roleKey, setRoleKey] = useState('all');
  // null = show both; true/false = filter
  const [activeOnly, setActiveOnly] = useState<boolean | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const role = ROLE_CHIPS.find((r) => r.key === roleKey)?.role || '';
  const query = useInternalUsers({
    search,
    role,
    isActive: activeOnly,
    page,
    pageSize,
  });

  const columns = useMemo<ColumnDef<InternalUserRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'User',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {row.original.name || row.original.username || `User #${row.original.id}`}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {row.original.username ? <>@{row.original.username}</> : null}
              {row.original.username && row.original.phone_number ? ' · ' : null}
              {row.original.phone_number ?? row.original.email ?? null}
            </div>
          </div>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.original.is_superuser ? (
              <Badge tone="primary">superuser</Badge>
            ) : null}
            {row.original.role ? (
              <Badge tone="neutral" className="capitalize">{row.original.role}</Badge>
            ) : row.original.is_staff ? (
              <Badge tone="neutral">staff (django)</Badge>
            ) : (
              <span className="text-xs text-muted-foreground/70">—</span>
            )}
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            {row.original.is_active ? (
              <Badge tone="success">active</Badge>
            ) : (
              <Badge tone="danger">inactive</Badge>
            )}
            {row.original.is_staff && !row.original.role ? (
              <Badge tone="info">internal</Badge>
            ) : null}
          </div>
        ),
      },
      {
        id: 'last_active',
        header: 'Last active',
        cell: ({ row }) =>
          row.original.last_active ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.last_active), 'MMM d, yyyy')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
      {
        id: 'joined',
        header: 'Joined',
        cell: ({ row }) =>
          row.original.date_joined ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.date_joined), 'MMM d, yyyy')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
    ],
    [],
  );

  const total = query.data?.total_items ?? query.data?.count ?? (query.data?.results?.length ?? 0);
  const rows = query.data?.results ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Settings"
        title="User Management"
        description="Edit profile details, view BI access and CRM rules linked to internal users."
        actions={
          <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)} className="gap-1">
            <Plus className="h-3.5 w-3.5" /> New user
          </Button>
        }
      />

      <div className="mt-4">
        <DataView<InternalUserRow>
          title="Internal users"
          data={rows}
          columns={columns}
          isLoading={query.isLoading}
          isError={query.isError}
          onRetry={() => void query.refetch()}
          search={{
            value: search,
            onChange: (v) => {
              setSearch(v);
              setPage(1);
            },
            placeholder: 'Search by name, username, phone, email…',
          }}
          pagination={{
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: (n) => {
              setPageSize(n);
              setPage(1);
            },
          }}
          onRowClick={(r) => navigate(`/users/${r.id}`)}
          maxHeight="62vh"
          columnToggles
          emptyTitle="No internal users match these filters"
          toolbar={
            <div className="flex flex-wrap items-center gap-1.5">
              {ROLE_CHIPS.slice(0, 6).map((r) => (
                <ToggleChip
                  key={r.key}
                  active={roleKey === r.key}
                  onClick={() => {
                    setRoleKey(r.key);
                    setPage(1);
                  }}
                >
                  {r.label}
                </ToggleChip>
              ))}
              <Select
                value={
                  ROLE_CHIPS.slice(0, 6).some((r) => r.key === roleKey)
                    ? '__any'
                    : roleKey
                }
                onValueChange={(v) => {
                  if (v === '__any') {
                    setRoleKey('all');
                  } else {
                    setRoleKey(v);
                  }
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-7 w-32 text-[11px]">
                  <SelectValue placeholder="More roles…" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_DROPDOWN.map((r) => (
                    <SelectItem key={r.value} value={r.value} className="capitalize">
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="mx-1 h-4 w-px bg-border" aria-hidden />
              {/* Cycles all → active → inactive → all. */}
              <ToggleChip
                active={activeOnly !== null}
                tone={activeOnly === false ? 'danger' : 'success'}
                onClick={() => setActiveOnly((v) => (v === null ? true : v === true ? false : null))}
              >
                {activeOnly === null ? 'all statuses' : activeOnly ? 'active only' : 'inactive only'}
              </ToggleChip>
            </div>
          }
        />
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
